import { throwRepoErrorFromApiV3Error } from '@webapp/core/utils/repositories/throwRepoErrorFromApiV3Error'
import { type RepoPaginationResponse } from '@webapp/infrastructure/interfaces/repositories/RepoPaginationResponse'
import { type IHttpClient } from '@webapp/infrastructure/interfaces/services/IHttpClient'
import {
  type ICreditCardAccountRepository,
  type ListCreditCardAccountsParams,
  type ListCreditCardAccountsResult,
} from '@webapp/modules/accounts-payable/business-logic/interfaces/ICreditCardAccountRepository'
import { type CreditCardAccountConstructor } from '@webapp/modules/accounts-payable/entities/CreditCardAccount'
import { type CreditCardAccountApiModel } from '../../api-models/CreditCardAccountApiModel'
import { mapCreditCardAccountApiModel } from '../../mappers/mapCreditCardAccountApiModel'

interface Dependency {
  httpClient: IHttpClient
}

interface ApiPaginationApiModel {
  count?: number
  page_size?: number
  num_pages?: number
  current_page?: number
}

interface ListCreditCardAccountsResponse {
  data: CreditCardAccountApiModel[]
  metadata?: { pagination?: ApiPaginationApiModel }
}

// Matches the 'ap-reconcile-accounts' pagination default (BHV-006).
const DEFAULT_ORDER_BY = 'name'

const mapPagination = (
  pagination: ApiPaginationApiModel | undefined
): RepoPaginationResponse => ({
  count: pagination?.count ?? 0,
  pageSize: pagination?.page_size ?? 10,
  numPages: pagination?.num_pages ?? 1,
  page: pagination?.current_page ?? 1,
})

export class CreditCardAccountRepository implements ICreditCardAccountRepository {
  constructor(private readonly deps: Dependency) {}

  async getById(id: number): Promise<CreditCardAccountConstructor> {
    try {
      const res = await this.deps.httpClient.get<{
        data: CreditCardAccountApiModel
      }>(`/api/v2/ap/creditcards/${id}/`)

      return mapCreditCardAccountApiModel(res.data.data)
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async list(
    params: ListCreditCardAccountsParams
  ): Promise<ListCreditCardAccountsResult> {
    try {
      const res =
        await this.deps.httpClient.get<ListCreditCardAccountsResponse>(
          `/api/v2/ap/creditcards/`,
          {
            params: {
              order_by: params.orderBy ?? DEFAULT_ORDER_BY,
              page: params.page ?? 1,
              page_size: params.pageSize ?? 10,
              ...(params.search ? { search: params.search } : {}),
            },
          }
        )

      return {
        accounts: res.data.data.map(mapCreditCardAccountApiModel),
        pagination: mapPagination(res.data.metadata?.pagination),
      }
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }
}
